import React, { useState } from 'react';
import { pcPrices } from '../priceData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Laptop } from 'lucide-react';

const PricesPC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const services = [
    { id: 'screen', title: 'Skjermbytte', data: pcPrices.screen },
    { id: 'keyboard', title: 'Tastatur', data: pcPrices.keyboard },
    { id: 'storage', title: 'SSD/HDD Oppgradering', data: pcPrices.storage },
    { id: 'ram', title: 'RAM Oppgradering', data: pcPrices.ram },
    { id: 'software', title: 'Programvare & Service', data: pcPrices.software },
    { id: 'other', title: 'Andre Tjenester', data: pcPrices.other }
  ];

  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter(item =>
      item.model.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <div className="min-h-screen pt-32 pb-16">
      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-50 to-cyan-50 py-16 mb-16">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-sky-500 to-cyan-500 rounded-2xl mb-6">
            <Laptop size={32} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            PC & Laptop <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-cyan-600">Priser</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Reparasjon og oppgradering av PC, Mac og Laptop
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 max-w-6xl">
        {/* Search */}
        <div className="mb-8">
          <Input
            type="text"
            placeholder="Søk etter tjeneste (f.eks. MacBook, SSD, Windows)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md mx-auto border-2 focus:border-sky-600"
          />
        </div>

        {/* Service Sections */}
        <div className="space-y-8">
          {services.map((service) => {
            const filteredData = filterData(service.data);
            if (filteredData.length === 0 && searchTerm) return null;

            return (
              <Card key={service.id} className="border-2">
                <CardHeader className="bg-gradient-to-r from-sky-50 to-cyan-50">
                  <CardTitle className="text-2xl">{service.title}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-4 font-semibold text-gray-900">Tjeneste</th>
                          <th className="text-right p-4 font-semibold text-gray-900">Pris</th>
                          <th className="text-right p-4 font-semibold text-gray-900">Tid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((item, index) => (
                          <tr
                            key={index}
                            className="border-b hover:bg-sky-50/50 transition-colors"
                          >
                            <td className="p-4 font-medium text-gray-900">{item.model}</td>
                            <td className="p-4 text-right font-semibold text-sky-600">{item.price}</td>
                            <td className="p-4 text-right text-gray-600">{item.time}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <Card className="bg-gradient-to-br from-sky-50 to-white border-2 border-sky-100">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-sky-600 mb-2">Gratis</div>
              <div className="font-semibold text-gray-900 mb-1">Diagnose</div>
              <p className="text-sm text-gray-600">Vi sjekker din PC gratis</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-50 to-white border-2 border-cyan-100">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-cyan-600 mb-2">1-3 dager</div>
              <div className="font-semibold text-gray-900 mb-1">Leveringstid</div>
              <p className="text-sm text-gray-600">De fleste reparasjoner</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-sky-50 to-white border-2 border-sky-100">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-sky-600 mb-2">2 år</div>
              <div className="font-semibold text-gray-900 mb-1">Garanti</div>
              <p className="text-sm text-gray-600">På deler og arbeid</p>
            </CardContent>
          </Card>
        </div>

        {/* Note */}
        <Card className="mt-8 border-l-4 border-l-sky-600">
          <CardContent className="p-6">
            <p className="text-gray-600 mb-3">
              <strong className="text-gray-900">Viktig:</strong> Prisene varierer avhengig av modell og komponentkvalitet. 
              "Fra" priser indikerer startpris, og endelig pris avhenger av din spesifikke PC/laptop modell.
            </p>
            <p className="text-gray-600">
              <strong className="text-gray-900">Vi reparerer:</strong> MacBook, Windows laptops, Gaming laptops, 
              Dell, HP, Lenovo, Asus, Acer, MSI og alle andre merker. Kontakt oss for eksakt pristilbud!
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PricesPC;
